// Flow types automatically removed

import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import { Component } from 'react';
import { Provider } from './helpers/queryParamsContext';

/**
 * This gets called and the return value is used as the default "search" prop in
 * QueryParamsProvider. This keeps it decoupled from the DOM until it
 * actually mounts. This also gets used within the initial values of the React
 * context.
 */
export function safelyGetLocationSearch() {
    if (typeof window !== 'undefined' && window.location.search) {
        return window.location.search;
    }

    return '';
}
/**
 * Used as the default implementation of updating the URL with query params and
 * gets set as the default prop "onSearchChange" in QueryParamsProvider.
 * This allows it to be decoupled from the DOM. Note that this does not get called
 * when a "popstate" event occurs. This is because the URL has already updated,
 * and the Provider's state just needs to be updated
 */

export function safelyWriteLocation(search) {
    if (typeof window === 'undefined') {
        return;
    }

    const { host, pathname, protocol } = window.location;
    const baseUrl = [protocol, '//', host, pathname].join('');
    const params = search ? search : ''; // already prepended `?`
    // using pushState so pressing back takes user to previous filter set

    window.history.pushState({}, '', handleLocaleUrl(baseUrl + params, GLOBAL_CLIENT_ONLY_LOCALE));
}
/**
 * Component meant to maintain the canonical state of the search string from
 * window.location and updating it on changes.
 *
 * Note: rename to something more relevant e.g. QueryParamsSync
 */

export class QueryParamsProvider extends Component {
    static defaultProps = {
        search: safelyGetLocationSearch(),
        onSearchChange: safelyWriteLocation,
    };

    constructor(props) {
        super(props);
        const search = props.search;
        const params = new URLSearchParams(search);
        this.state = {
            search,
            params,
        };
    }

    componentDidMount() {
        window.addEventListener('popstate', this.onPopState);
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.onPopState);
    }
    /**
     * Event handler for when a `popstate` event occurs in order to keep state in
     * sync with the change in the search portion of the URL.
     */

    onPopState = e => {
        const search = e.currentTarget.location.search;
        const params = new URLSearchParams(search);
        this.setState({
            search,
            params,
        });
    };
    /**
     * Gets the context values meant to be passed to the Consumer
     */

    getContextValue = () => {
        return {
            getQueryParam: key => this.state.params.get(key),
            setQueryParam: this.setQueryParam,
            mergeQueryParamsObj: this.mergeQueryParamsObj,
            reset: this.unmountConsumer,
        };
    };
    /**
     * Sets a single query param in state and then updating the URL by calling the
     * prop `onSearchChange`.
     */

    setQueryParam = (key, param) => {
        this.setState(prevState => {
            const nextParams = new URLSearchParams(prevState.params);

            if (!param || (Array.isArray(param) && !param.length)) {
                nextParams.delete(key);
            } else {
                nextParams.set(key, param.toString());
            }

            const searchQuery = nextParams.toString();
            return {
                search: searchQuery ? `?${nextParams.toString()}` : '',
                params: nextParams,
            };
        }, this.updateSearchCallback);
    };
    /**
     * Takes an object and does a shallow merge of it with the current state of
     * query params which then updates the URL by calling the prop
     * `onSearchChange`.
     */

    mergeQueryParamsObj = params => {
        this.setState(prevState => {
            const nextParams = new URLSearchParams(prevState.params);

            for (const paramKey in params) {
                if (
                    !params[paramKey] ||
                    (Array.isArray(params[paramKey]) && !params[paramKey].length)
                ) {
                    nextParams.delete(paramKey);
                } else {
                    nextParams.set(paramKey, params[paramKey].toString());
                }
            }

            const searchQuery = nextParams.toString();
            return {
                search: searchQuery ? `?${nextParams.toString()}` : '',
                params: nextParams,
            };
        }, this.updateSearchCallback);
    };
    updateSearchCallback = () => this.props.onSearchChange(this.state.search);
    unmountConsumer = () =>
        this.setState({
            search: '',
            params: new URLSearchParams(),
        });

    render() {
        return <Provider value={this.getContextValue()}>{this.props.children}</Provider>;
    }
}
